/**
 * Business Capabilities List Route
 *
 * Displays searchable, filterable list of business capabilities
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useState } from 'react';
import { usePermission } from '../../hooks/usePermission';
import { useListPageState } from '../../hooks/useListPageState';
import { useBulkOperations } from '../../hooks/useBulkOperations';
import { useCapabilities } from '../../graphql/queries';
import {
  DataTable,
  StatusBadge,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  BulkActionToolbar,
  BulkDeleteConfirmationDialog,
  BulkUpdateDialog,
  type DataTableColumn,
  type BulkUpdateFieldConfig,
} from '../../components/shared';
import type {
  BusinessCapability,
  CapabilityCriticality,
  CapabilityLifecycleStatus,
} from '../../graphql/types';
import { bulkDeleteCapabilities, bulkUpdateCapabilities } from '../../lib/apiClient';
import { exportToCSV, exportToJSON } from '../../lib/exportUtils';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/capabilities/')({
  component: CapabilitiesListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

export { CapabilitiesListPage };

interface CapabilityFilters {
  search: string;
  lifecycleStatus: CapabilityLifecycleStatus | '';
  criticality: CapabilityCriticality | '';
}

const CAPABILITY_BULK_FIELDS: BulkUpdateFieldConfig[] = [
  {
    key: 'criticality',
    label: 'Criticality',
    type: 'select',
    options: [
      { value: 'critical', label: 'Critical' },
      { value: 'important', label: 'Important' },
      { value: 'supporting', label: 'Supporting' },
      { value: 'nice_to_have', label: 'Nice to Have' },
    ],
  },
  {
    key: 'strategicImportance',
    label: 'Strategic Importance',
    type: 'select',
    options: [
      { value: 'strategic', label: 'Strategic' },
      { value: 'core', label: 'Core' },
      { value: 'supporting', label: 'Supporting' },
    ],
  },
  {
    key: 'lifecycleStatus',
    label: 'Lifecycle Status',
    type: 'select',
    options: [
      { value: 'planned', label: 'Planned' },
      { value: 'developing', label: 'Developing' },
      { value: 'active', label: 'Active' },
      { value: 'mature', label: 'Mature' },
      { value: 'deprecated', label: 'Deprecated' },
      { value: 'retired', label: 'Retired' },
    ],
  },
  {
    key: 'investmentPriority',
    label: 'Investment Priority',
    type: 'select',
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
      { value: 'hold', label: 'Hold' },
    ],
  },
];

const ForgeButton = 'forge-button' as React.ElementType;

function CapabilitiesListPage(): JSX.Element {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { page, pageSize } = Route.useSearch();

  const [filters, setFilters] = useState<CapabilityFilters>({
    search: '',
    lifecycleStatus: '',
    criticality: '',
  });

  const canWriteCapability = usePermission('capability:delete');

  const { data, loading, error } = useCapabilities();

  const capabilities = data?.capabilities?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, filteredItems, pagedItems, totalCount } =
    useListPageState<BusinessCapability>({
      items: capabilities,
      page,
      pageSize,
      filterFn: (cap) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = cap.name.toLowerCase().includes(searchLower);
          const matchesDescription = cap.description?.toLowerCase().includes(searchLower);
          if (!matchesName && !matchesDescription) return false;
        }
        if (filters.lifecycleStatus && cap.lifecycleStatus !== filters.lifecycleStatus)
          return false;
        if (filters.criticality && cap.criticality !== filters.criticality) return false;
        return true;
      },
      sortValue: (cap, column) => String(cap[column as keyof BusinessCapability] ?? ''),
    });

  const bulk = useBulkOperations<BusinessCapability, Parameters<typeof bulkUpdateCapabilities>[1]>({
    rows: filteredItems,
    getRowId: (cap) => cap.entityId,
    getRowName: (cap) => cap.name,
    bulkDelete: (ids) => bulkDeleteCapabilities(ids),
    bulkUpdate: (ids, updates) => bulkUpdateCapabilities(ids, updates),
    refetchQueries: ['ListCapabilities'],
    exportFileName: 'capabilities-export',
  });

  const columns: DataTableColumn<BusinessCapability>[] = [
    { key: 'name', label: 'Name', sortable: true, accessor: (cap) => cap.name },
    {
      key: 'criticality',
      label: 'Criticality',
      sortable: true,
      render: (cap) => <StatusBadge value={cap.criticality} variant="lifecycle" />,
    },
    {
      key: 'strategicImportance',
      label: 'Strategic Importance',
      sortable: true,
      render: (cap) => <StatusBadge value={cap.strategicImportance} variant="lifecycle" />,
    },
    {
      key: 'lifecycleStatus',
      label: 'Status',
      sortable: true,
      render: (cap) => <StatusBadge value={cap.lifecycleStatus} variant="lifecycle" />,
    },
    { key: 'level', label: 'Level', sortable: true, accessor: (cap) => cap.level },
    {
      key: 'owningBusinessUnitId',
      label: 'Business Unit',
      sortable: true,
      accessor: (cap) => cap.owningBusinessUnitId,
    },
  ];

  const handleRowClick = (cap: BusinessCapability): void => {
    navigate({ to: '/capabilities/$capabilityId', params: { capabilityId: cap.entityId } });
  };

  const handleCreate = (): void => {
    navigate({ to: '/capabilities/new' });
  };

  const handleExportCSV = (): void => {
    exportToCSV(filteredItems as object[] as Record<string, unknown>[], 'business-capabilities');
  };

  const handleExportJSON = (): void => {
    exportToJSON(filteredItems, 'business-capabilities');
  };

  if (loading) {
    return (
      <div className="capabilities-list-page">
        <TableSkeleton rows={10} columns={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="capabilities-list-page">
        <EmptyState
          icon="error"
          title="Failed to load capabilities"
          description={error.message}
          actionLabel="Retry"
          onAction={() => apolloClient.refetchQueries({ include: ['ListCapabilities'] })}
        />
      </div>
    );
  }

  return (
    <div className="capabilities-list-page">
      <div className="page-header page-header--toolbar page-header--spaced">
        <div className="page-header-title">
          <h1 className="forge-typography--headline4">Business Capabilities</h1>
          <span className="page-header-count forge-typography--body2">
            {totalCount} {totalCount === 1 ? 'capability' : 'capabilities'}
          </span>
        </div>
        <div className="page-header-actions">
          <PermissionGate permission="capability:create">
            <ForgeButton type="button" variant="raised" onClick={handleCreate}>
              New Capability
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search capabilities..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            aria-label="Search capabilities"
          />
        </div>

        <div className="filter-selects">
          <select
            className="filter-select"
            value={filters.lifecycleStatus}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                lifecycleStatus: e.target.value as CapabilityLifecycleStatus | '',
              }))
            }
            aria-label="Filter by lifecycle status"
          >
            <option value="">All Statuses</option>
            <option value="planned">Planned</option>
            <option value="developing">Developing</option>
            <option value="active">Active</option>
            <option value="mature">Mature</option>
            <option value="deprecated">Deprecated</option>
            <option value="retired">Retired</option>
          </select>

          <select
            className="filter-select"
            value={filters.criticality}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                criticality: e.target.value as CapabilityCriticality | '',
              }))
            }
            aria-label="Filter by criticality"
          >
            <option value="">All Criticalities</option>
            <option value="critical">Critical</option>
            <option value="important">Important</option>
            <option value="supporting">Supporting</option>
            <option value="nice_to_have">Nice to Have</option>
          </select>
        </div>

        <div className="export-actions">
          <ForgeButton
            type="button"
            variant="outlined"
            onClick={handleExportCSV}
            title="Export as CSV"
          >
            CSV
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="outlined"
            onClick={handleExportJSON}
            title="Export as JSON"
          >
            JSON
          </ForgeButton>
        </div>
      </div>

      {/* Bulk Action Toolbar */}
      {canWriteCapability && bulk.selectedIds.size > 0 && (
        <BulkActionToolbar
          selectedCount={bulk.selectedIds.size}
          onBulkDelete={() => bulk.setBulkDeleteOpen(true)}
          onBulkUpdate={() => bulk.setBulkUpdateOpen(true)}
          onClearSelection={bulk.clearSelection}
          onBulkExport={bulk.handleBulkExport}
        />
      )}

      {/* Data Table */}
      {filteredItems.length === 0 ? (
        <EmptyState
          icon="architecture"
          title="No capabilities found"
          description={
            filters.search || filters.lifecycleStatus || filters.criticality
              ? 'Try adjusting your filters'
              : 'Get started by creating your first business capability'
          }
          actionLabel={canWriteCapability ? 'Create Capability' : undefined}
          onAction={canWriteCapability ? handleCreate : undefined}
        />
      ) : (
        <DataTable
          data={pagedItems}
          columns={columns}
          getRowKey={(cap) => cap.entityId}
          onRowClick={handleRowClick}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          selectable={canWriteCapability}
          selectedIds={bulk.selectedIds}
          onSelectionChange={bulk.setSelectedIds}
        />
      )}

      {/* Pagination */}
      <Pagination
        currentPage={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={(newPage) =>
          navigate({ to: '/capabilities', search: { page: newPage, pageSize } })
        }
        onPageSizeChange={(newSize) =>
          navigate({ to: '/capabilities', search: { page: 1, pageSize: newSize } })
        }
      />

      {/* Dialogs */}
      <BulkDeleteConfirmationDialog
        open={bulk.bulkDeleteOpen}
        itemNames={bulk.selectedNames}
        onConfirm={() => void bulk.handleBulkDelete()}
        onCancel={() => bulk.setBulkDeleteOpen(false)}
        isDeleting={bulk.isBulkDeleting}
        result={bulk.bulkDeleteResult}
        onDone={bulk.handleBulkDeleteDone}
      />

      <BulkUpdateDialog
        open={bulk.bulkUpdateOpen}
        selectedCount={bulk.selectedIds.size}
        entityLabel={{ singular: 'capability', plural: 'capabilities' }}
        fields={CAPABILITY_BULK_FIELDS}
        idPrefix="bulk-cap"
        selectClassName="filter-select"
        onConfirm={(updates) =>
          void bulk.handleBulkUpdate(updates as Parameters<typeof bulkUpdateCapabilities>[1])
        }
        onCancel={() => bulk.setBulkUpdateOpen(false)}
        isUpdating={bulk.isBulkUpdating}
      />
    </div>
  );
}
